import { FC } from 'react'
import { PriceCard } from '@/root/modules/shared/SelectTicketPage'
import type { ResidentOverrideToggle } from '@/root/modules/shared/SelectTicketPage/components/PriceCard/PriceCard'
import type { ResidentOverrideCategory } from '@/root/modules/shared/SelectTicketPage/helpers/residentOverride/residentOverride'
import { Price } from '@/root/shared-types/dto/SailPricing'
import { CountryItem } from '@/root/shared-types'
import { PassengerFormData } from '@/root/shared-types/selectTicketPage/selectTicket'

interface PriceCardListProps {
  maxTicketQty: number
  maxTicketQtyByCode?: Record<string, number>
  prices: Array<Price>
  countries?: CountryItem[]
  departureDate?: string
  disabled?: boolean
  hidePassengerDataForm?: boolean
  initialPassengersByCode?: Record<string, PassengerFormData[]>
  initialQuantityByCode?: Record<string, number>
  onDec?: (price: Price) => void | Promise<void>
  onFormStateChange?: (priceCode: string, saved: number, total: number) => void
  onInc?: (price: Price) => void | Promise<void>
  onPassengerDeleted?: (
    price: Price,
    slotIndex: number,
    verifiedPriceCategory?: string,
    seqN?: number
  ) => void | Promise<void>
  onPassengerSaved?: (priceCode: string, slotIndex: number, data: PassengerFormData) => void | Promise<void>
  onResidentDec?: (price: Price) => void | Promise<void>
  onResidentIdVerified?: (
    price: Price,
    slotIndex: number,
    personalId: string,
    priceCategory: string
  ) => Promise<number | null>
  onResidentInc?: (price: Price) => void | Promise<void>
  onResidentOverrideToggled?: ResidentOverrideToggle
  onSetQuantity?: (price: Price, qty: number) => Promise<void>
  priceCategoryTitlesByCode?: Readonly<Record<string, string>>
  residentOverride?: ResidentOverrideCategory
  priceFilter?: (price: Price) => boolean
  route?: string
  seqNsByCode?: Record<string, number[]>
}

export const PriceCardList: FC<PriceCardListProps> = ({
  prices,
  countries = [],
  priceFilter,
  route,
  departureDate,
  maxTicketQty,
  maxTicketQtyByCode,
  onInc,
  onDec,
  onResidentInc,
  onResidentDec,
  onResidentIdVerified,
  onResidentOverrideToggled,
  residentOverride,
  onPassengerSaved,
  onFormStateChange,
  onPassengerDeleted,
  onSetQuantity,
  priceCategoryTitlesByCode,
  disabled,
  hidePassengerDataForm,
  initialQuantityByCode,
  initialPassengersByCode,
  seqNsByCode,
}) => {
  const filteredByPrice = prices.filter(priceFilter ?? (() => true))
  const isResidentMode = filteredByPrice.length > 0 && filteredByPrice.every((p) => p.priceCategory.isResident)
  const pricesToRender = isResidentMode ? filteredByPrice.slice(0, 1) : filteredByPrice

  if (!pricesToRender.length) {
    return null
  }

  return (
    <ul className="flex w-full flex-col gap-5">
      {pricesToRender.map((price) => (
        <div key={'price-' + price.priceCategory.code} className="flex flex-col gap-2">
          <PriceCard
            price={price}
            countries={countries}
            route={route}
            departureDate={departureDate}
            maxTicketQty={maxTicketQtyByCode?.[price.priceCategory.code] ?? maxTicketQty}
            onInc={onInc}
            onDec={onDec}
            onResidentInc={onResidentInc}
            onResidentDec={onResidentDec}
            onResidentIdVerified={onResidentIdVerified}
            onResidentOverrideToggled={onResidentOverrideToggled}
            residentOverride={residentOverride}
            onPassengerSaved={onPassengerSaved}
            onFormStateChange={onFormStateChange}
            onPassengerDeleted={onPassengerDeleted}
            onSetQuantity={onSetQuantity}
            priceCategoryTitlesByCode={priceCategoryTitlesByCode}
            disabled={disabled}
            hidePassengerDataForm={hidePassengerDataForm}
            initialQuantity={initialQuantityByCode?.[price.priceCategory.code]}
            initialSavedPassengers={initialPassengersByCode?.[price.priceCategory.code]}
            seqNs={seqNsByCode?.[price.priceCategory.code]}
          />
        </div>
      ))}
    </ul>
  )
}

export default PriceCardList
